// Auth client state — the session tokens and derived auth flag. The tokens are
// the source of truth for "logged in" across the whole app: the router guard
// and the axios interceptors both read the same localStorage keys, so this
// store writes through to them (never a parallel copy). Refs mirror the keys so
// components can react to login / logout without polling storage.
//
// Server state (the sign-in requests themselves) lives in TanStack Query — this
// store only holds the resulting session, plus the in-flight email OTP
// challenge that the OTP step needs to read after the login step navigates.
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import localStorageService from '@core/persistence/localStorage/localStorageService'
import { AUTH_STORAGE_KEYS } from '@core/persistence/storageKeys'
import type { EmailOtpChallenge } from '../types/authTypes'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorageService.getItem(AUTH_STORAGE_KEYS.TOKEN))
  const refreshToken = ref<string | null>(
    localStorageService.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
  )

  // The in-flight email OTP challenge, held only between the email step and the
  // OTP step. Deliberately NOT persisted: a verification id is a short-lived
  // credential, and leaving it in storage would let a stale code survive a
  // reload or another tab. A refresh on the OTP step therefore drops the
  // challenge, and that container sends the user back to /login to restart.
  const otpChallenge = ref<EmailOtpChallenge | null>(null)
  const otpEmail = ref<string | null>(null)

  const isAuthenticated = computed(() => Boolean(token.value))

  // Persist a freshly issued session (UAE Pass login or sign-up). Writes the two
  // keys the interceptors + guard read, so the rest of the app is logged in.
  function setSession(accessToken: string, newRefreshToken: string): void {
    token.value = accessToken
    refreshToken.value = newRefreshToken
    localStorageService.setItem(AUTH_STORAGE_KEYS.TOKEN, accessToken)
    localStorageService.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken)
    clearOtpChallenge()
  }

  // `email` is kept only to show the user which inbox to check on the OTP step.
  function startOtpChallenge(challenge: EmailOtpChallenge, email: string): void {
    otpChallenge.value = challenge
    otpEmail.value = email
  }

  // A resend may mint a new verification id; the old one stops verifying, so the
  // store must adopt the new one or the next submit fails.
  function updateOtpVerificationId(verificationId: string): void {
    if (!otpChallenge.value) return
    otpChallenge.value = { ...otpChallenge.value, verificationId }
  }

  function clearOtpChallenge(): void {
    otpChallenge.value = null
    otpEmail.value = null
  }

  function clearSession(): void {
    token.value = null
    refreshToken.value = null
    localStorageService.removeItem(AUTH_STORAGE_KEYS.TOKEN)
    localStorageService.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    clearOtpChallenge()
  }

  return {
    token,
    refreshToken,
    otpChallenge,
    otpEmail,
    isAuthenticated,
    setSession,
    clearSession,
    startOtpChallenge,
    updateOtpVerificationId,
    clearOtpChallenge,
  }
})
